import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, } from "react";
import GoogleAnalytics from "react-ga";
import environment from "Configs/environment";
import { analytics, apps, initializeApp, performance } from "firebase/app";
import { MemberRole } from "domain-entities";

const GOOGLE_ANALYTICS_TRACKER_NAME = "partners";
const GOOGLE_ANALYTICS_CATEGORY = "Partners";

export enum TrackingPage {
    EXTERNAL_CHAT = "/external-chat",
    ROOT = "/",
}

export enum TrackingEvent {
    FAB_CLICKED = "partners_FAB_clicked",
    START_AUTH_CTA_CLICKED = "partners_startAuthCTA_clicked",
    AUTHENTICATION_SUCCEEDED = "partners_authenticate_succeeded",
    NEW_PARTNER_REQUEST_CLICKED = "partners_newPartnerRequest_clicked",
    NEW_PARTNER_REQUEST_SUCCESS = "partners_newPartnerRequest_success",
}

export enum PerformanceTrace {
    COLLECTION_EXTERNAL_CHAT_LOADED = "collection_externalChat_loaded",
}

export enum PerformanceTraceAttributeAndMetric {
    EXTERNAL_CHAT_MESSAGE_COUNT = "messageCount"
}

interface UpdateViewerAttributes {
    userId: string;
    companyId: string;
    role?: MemberRole;
}

interface Tracking {
    updateViewer: (attributes: UpdateViewerAttributes) => Promise<void>;
    trackPage: (path: TrackingPage) => void;
    trackEvent: (
        eventName: TrackingEvent,
        message?: string,
        params?: object
    ) => void;
    trackTrace: (name: string) => performance.Trace
}

const TrackingContext = createContext<Tracking>(null as any);

interface Props {
    children: JSX.Element;
}

const TrackingProvider: FC<Props> = (props) => {
    const firebaseAppRef = useRef(
        apps.length ? apps[0] : initializeApp(environment.firebaseConfig)
    );
    const performanceRef = useRef(performance());
    const firebaseAnalyticsRef = useRef(analytics(firebaseAppRef.current));
    const userAttributesRef = useRef<UpdateViewerAttributes>();
    const initializeSdks = useCallback(() => {
        if (environment.googleTagManager.trackingId) {
            GoogleAnalytics.initialize(environment.googleTagManager.trackingId, {
                alwaysSendToDefaultTracker: false,
                gaOptions: {
                    name: GOOGLE_ANALYTICS_TRACKER_NAME,
                },
            });
        }
    }, []);

    useEffect(initializeSdks, []);

    const updateViewer = useCallback(
        async (attributes: UpdateViewerAttributes) => {
            GoogleAnalytics.set(attributes);

            firebaseAnalyticsRef.current.setUserId(attributes.userId);
            firebaseAnalyticsRef.current.setUserProperties({
                companyId: attributes.companyId,
                role: attributes.role,
            });
            userAttributesRef.current = {...attributes};
        },
        []
    );

    const trackPage = useCallback((path: TrackingPage) => {
        const validPath = path === TrackingPage.ROOT ? TrackingPage.EXTERNAL_CHAT : path;

        GoogleAnalytics.pageview(validPath, [GOOGLE_ANALYTICS_TRACKER_NAME]);
        firebaseAnalyticsRef.current.setCurrentScreen(validPath);
    }, []);

    const trackEvent = useCallback(
        (eventName: TrackingEvent, message?: string, params?: object) => {
            GoogleAnalytics.event(
                {
                    category: GOOGLE_ANALYTICS_CATEGORY,
                    action: eventName,
                    label: message,
                },
                [GOOGLE_ANALYTICS_TRACKER_NAME]
            );

            firebaseAnalyticsRef.current.logEvent(eventName, {
                ...(message ? {eventMessage: message} : {}),
                ...(userAttributesRef.current || {}),
                ...(params || {}),
            });
        },
        []
    );

    const trackTrace = useMemo(() => performanceRef.current.trace, []);

    const tracking: Tracking = {
        updateViewer,
        trackPage,
        trackEvent,
        trackTrace
    };

    return (
        <TrackingContext.Provider value={tracking}>
            {props.children}
        </TrackingContext.Provider>
    );
};

export default TrackingProvider;

export function useTracking() {
    return useContext(TrackingContext);
}
